import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Link2, Hourglass, BadgeCheck, BadgeX, CreditCard } from 'lucide-react';
import { Button } from './ui/button';
import { formatTime } from '../lib/dateUtils';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_MS = 15000;

const StatTile = ({ icon: Icon, label, value, hint, tone, testId }) => (
  <div className={`rounded-2xl border p-4 sm:p-5 ${tone}`} data-testid={testId}>
    <div className="flex items-center gap-2 text-xs uppercase tracking-wide opacity-80">
      <Icon className="w-4 h-4" /> {label}
    </div>
    <p className="text-3xl sm:text-4xl font-bold mt-2 tabular-nums" data-testid={`${testId}-value`}>{value ?? '–'}</p>
    {hint && <p className="text-xs mt-1 opacity-75">{hint}</p>}
  </div>
);

export const LiveStats = ({ token }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/live-stats`, { headers: { Authorization: `Bearer ${token}` } });
      setStats(res.data);
      setError(false);
    } catch {
      setError(true);
    }
  }, [token]);

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  const totalVehicles = stats ? stats.vehicles_paid + stats.vehicles_not_paid : 0;

  return (
    <section className="bg-white border rounded-2xl shadow-sm p-4 sm:p-6" data-testid="live-stats">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className={`absolute inline-flex h-full w-full rounded-full opacity-75 ${error ? 'bg-red-400' : 'bg-emerald-400 animate-ping'}`} />
              <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${error ? 'bg-red-500' : 'bg-emerald-500'}`} />
            </span>
            Live overview
          </h2>
          <p className="text-xs text-muted-foreground" data-testid="live-stats-updated">
            {error ? 'Could not refresh — retrying…' : stats ? `Updates every 15 seconds · last update ${formatTime(stats.updated_at)} · month ${stats.month}` : 'Loading…'}
          </p>
        </div>
        <Button variant="outline" className="rounded-full" onClick={() => navigate('/subscriptions')} data-testid="open-subscriptions-btn">
          <CreditCard className="w-4 h-4 mr-2" /> Manage subscriptions
        </Button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile icon={Link2} label="Parents linked" value={stats?.parents_linked} hint="linked to a transport operator" tone="bg-emerald-50 text-emerald-800 border-emerald-200" testId="live-parents-linked" />
        <StatTile icon={Hourglass} label="Parents waiting" value={stats?.parents_waiting} hint={stats ? `${stats.parents_pending_requests} with a pending link request` : ''} tone="bg-amber-50 text-amber-800 border-amber-200" testId="live-parents-waiting" />
        <StatTile icon={BadgeCheck} label="Operators paid" value={stats?.operators_paid} hint={stats ? `${stats.vehicles_paid} of ${totalVehicles} vehicles paid this month` : ''} tone="bg-sky-50 text-sky-800 border-sky-200" testId="live-operators-paid" />
        <StatTile icon={BadgeX} label="Operators not paid" value={stats?.operators_not_paid} hint={stats ? `at least one vehicle not paid${stats.operators_without_vehicles ? ` · ${stats.operators_without_vehicles} operator(s) have no vehicles yet` : ''}` : ''} tone="bg-red-50 text-red-800 border-red-200" testId="live-operators-not-paid" />
      </div>
    </section>
  );
};
